import React, {
    useCallback,
    useEffect,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiChevronLeft,
    FiChevronRight,
    FiFileText,
    FiRefreshCw,
    FiSearch,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    fetchSalesGstRegister,

    salesGstRecordsSelector,
    salesGstSummarySelector,
    salesGstPaginationSelector,

    gstReportLoadingSelector,
    gstReportErrorSelector,
} from "../../../../../../../store/billing/gst/gstReportSlice";


/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};


const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        toNumber(value)
    );


const formatDate = (
    value
) => {
    if (
        !value
    ) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day:
                "2-digit",

            month:
                "short",

            year:
                "numeric",
        }
    ).format(
        date
    );
};


const formatText = (
    value
) => {
    if (
        !value
    ) {
        return "-";
    }

    return String(
        value
    )
        .replace(
            /_/g,
            " "
        )
        .toUpperCase();
};


/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const SalesGstRegister = ({
    fromDate,
    toDate,
    financialYear,
}) => {
    const dispatch =
        useDispatch();


    /*
    |--------------------------------------------------------------------------
    | REDUX
    |--------------------------------------------------------------------------
    */

    const records =
        useSelector(
            salesGstRecordsSelector
        ) || [];

    const summary =
        useSelector(
            salesGstSummarySelector
        ) || {};

    const pagination =
        useSelector(
            salesGstPaginationSelector
        ) || {};

    // const warning =
    //     useSelector(
    //         salesGstWarningSelector
    //     );

    const loading =
        useSelector(
            gstReportLoadingSelector
        ) || {};

    const errors =
        useSelector(
            gstReportErrorSelector
        ) || {};


    /*
    |--------------------------------------------------------------------------
    | LOCAL FILTERS
    |--------------------------------------------------------------------------
    */

    const [
        searchInput,
        setSearchInput,
    ] = useState("");

    const [
        search,
        setSearch,
    ] = useState("");

    const [
        gstin,
        setGstin,
    ] = useState("");

    const [
        status,
        setStatus,
    ] = useState(
        "CONFIRMED"
    );

    const [
        taxType,
        setTaxType,
    ] = useState(
        "ALL"
    );

    const [
        customerType,
        setCustomerType,
    ] = useState(
        "ALL"
    );

    const [
        supplyType,
        setSupplyType,
    ] = useState(
        "ALL"
    );


    /*
    |--------------------------------------------------------------------------
    | PAGINATION
    |--------------------------------------------------------------------------
    */

    const [
        page,
        setPage,
    ] = useState(1);

    const [
        limit,
        setLimit,
    ] = useState(20);


    /*
    |--------------------------------------------------------------------------
    | LOAD SALES GST REGISTER
    |--------------------------------------------------------------------------
    */

    const loadData =
        useCallback(
            async () => {
                await dispatch(
                    fetchSalesGstRegister({
                        page,

                        limit,

                        fromDate,

                        toDate,

                        financialYear,

                        status,

                        taxType,

                        customerType,

                        supplyType,

                        gstin,

                        search,
                    })
                );
            },
            [
                dispatch,

                page,
                limit,

                fromDate,
                toDate,
                financialYear,

                status,
                taxType,
                customerType,
                supplyType,
                gstin,
                search,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | LOAD ON FILTER CHANGE
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            loadData();
        },
        [
            loadData,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | RESET PAGE WHEN COMMON PERIOD CHANGES
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            setPage(1);
        },
        [
            fromDate,
            toDate,
            financialYear,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | SEARCH DEBOUNCE
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            const timer =
                setTimeout(
                    () => {
                        setSearch(
                            searchInput.trim()
                        );

                        setPage(1);
                    },
                    350
                );

            return () => {
                clearTimeout(
                    timer
                );
            };
        },
        [
            searchInput,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | PAGINATION VALUES
    |--------------------------------------------------------------------------
    */

    const total =
        toNumber(
            pagination?.total
        );

    const currentPage =
        toNumber(
            pagination?.page,
            page
        ) || 1;

    const currentLimit =
        toNumber(
            pagination?.limit,
            limit
        ) || 20;

    const totalPages =
        Math.max(
            toNumber(
                pagination?.totalPages,
                1
            ),
            1
        );

    const fromRecord =
        total > 0
            ? (
                currentPage -
                1
            ) *
                currentLimit +
                1
            : 0;

    const toRecord =
        total > 0
            ? Math.min(
                currentPage *
                    currentLimit,
                total
            )
            : 0;


    /*
    |--------------------------------------------------------------------------
    | RESET FILTERS
    |--------------------------------------------------------------------------
    */

    const handleResetFilters =
        () => {
            setSearchInput(
                ""
            );

            setSearch(
                ""
            );

            setGstin(
                ""
            );

            setStatus(
                "CONFIRMED"
            );

            setTaxType(
                "ALL"
            );

            setCustomerType(
                "ALL"
            );

            setSupplyType(
                "ALL"
            );

            setPage(
                1
            );
        };


    /*
    |--------------------------------------------------------------------------
    | CHANGE FILTER
    |--------------------------------------------------------------------------
    */

    const handleFilterChange =
        (
            setter,
            value
        ) => {
            setter(
                value
            );

            setPage(
                1
            );
        };


    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <div className="gst-sales-register">

            {/* =====================================================
                ERROR
            ===================================================== */}

            {errors.salesRegister && (

                <div className="gst-dashboard-alert">

                    <FiAlertCircle />

                    <span>
                        {errors.salesRegister}
                    </span>

                </div>

            )}


            {/* =====================================================
                WARNING
            ===================================================== */}

            {/* {warning && (

                <div className="gst-sales-warning">

                    <FiAlertCircle />

                    <span>
                        {warning}
                    </span>

                </div>

            )} */}


            {/* =====================================================
                MAIN SUMMARY
            ===================================================== */}

            <section className="gst-dashboard-summary-grid">

                <div className="gst-dashboard-summary-card">

                    <span>
                        Sales Documents
                    </span>

                    <strong>
                        {toNumber(
                            summary
                                ?.totalDocuments
                        )}
                    </strong>

                    <small>
                        GST sales invoices
                    </small>

                </div>


                <div className="gst-dashboard-summary-card">

                    <span>
                        Taxable Sales
                    </span>

                    <strong>
                        {formatCurrency(
                            summary
                                ?.taxableAmount
                        )}
                    </strong>

                    <small>
                        Total taxable outward supply
                    </small>

                </div>


                <div className="gst-dashboard-summary-card">

                    <span>
                        Output GST
                    </span>

                    <strong>
                        {formatCurrency(
                            summary
                                ?.outputTax
                        )}
                    </strong>

                    <small>
                        GST collected on sales
                    </small>

                </div>


                <div className="gst-dashboard-summary-card">

                    <span>
                        Invoice Total
                    </span>

                    <strong>
                        {formatCurrency(
                            summary
                                ?.grandTotal
                        )}
                    </strong>

                    <small>
                        Total sales invoice value
                    </small>

                </div>

            </section>


            {/* =====================================================
                TAX SUMMARY
            ===================================================== */}

            <section className="gst-dashboard-stat-grid gst-sales-tax-summary">

                <div>

                    <span>
                        CGST
                    </span>

                    <strong>
                        {formatCurrency(
                            summary
                                ?.cgstAmount
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        SGST
                    </span>

                    <strong>
                        {formatCurrency(
                            summary
                                ?.sgstAmount
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        IGST
                    </span>

                    <strong>
                        {formatCurrency(
                            summary
                                ?.igstAmount
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        Cess
                    </span>

                    <strong>
                        {formatCurrency(
                            summary
                                ?.cessAmount
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        B2B Taxable
                    </span>

                    <strong>
                        {formatCurrency(
                            summary
                                ?.b2bTaxableAmount
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        B2C Taxable
                    </span>

                    <strong>
                        {formatCurrency(
                            summary
                                ?.b2cTaxableAmount
                        )}
                    </strong>

                </div>

            </section>


            {/* =====================================================
                CLASSIFICATION SUMMARY
            ===================================================== */}

            <section className="gst-sales-classification">

                <div className="gst-sales-classification-card">

                    <span>
                        B2B Documents
                    </span>

                    <strong>
                        {toNumber(
                            summary
                                ?.registeredCustomers
                        )}
                    </strong>

                    <small>
                        Registered customers
                    </small>

                </div>


                <div className="gst-sales-classification-card">

                    <span>
                        B2C Documents
                    </span>

                    <strong>
                        {toNumber(
                            summary
                                ?.unregisteredCustomers
                        )}
                    </strong>

                    <small>
                        Unregistered customers
                    </small>

                </div>


                <div className="gst-sales-classification-card">

                    <span>
                        Inter-State
                    </span>

                    <strong>
                        {formatCurrency(
                            summary
                                ?.interStateTaxableAmount
                        )}
                    </strong>

                    <small>
                        IGST outward supply
                    </small>

                </div>


                <div className="gst-sales-classification-card">

                    <span>
                        Intra-State
                    </span>

                    <strong>
                        {formatCurrency(
                            summary
                                ?.intraStateTaxableAmount
                        )}
                    </strong>

                    <small>
                        CGST + SGST outward supply
                    </small>

                </div>

            </section>


            {/* =====================================================
                FILTER TOOLBAR
            ===================================================== */}

            <section className="gst-sales-toolbar">

                {/* SEARCH */}

                <div className="gst-sales-search">

                    <FiSearch />

                    <input
                        type="text"
                        value={
                            searchInput
                        }
                        placeholder="Search invoice, customer, GSTIN..."
                        onChange={(
                            event
                        ) =>
                            setSearchInput(
                                event
                                    .target
                                    .value
                            )
                        }
                    />

                </div>


                {/* GSTIN */}

                <input
                    type="text"
                    value={
                        gstin
                    }
                    placeholder="Customer GSTIN"
                    onChange={(
                        event
                    ) =>
                        handleFilterChange(
                            setGstin,
                            event
                                .target
                                .value
                        )
                    }
                />


                {/* STATUS */}

                <select
                    value={
                        status
                    }
                    onChange={(
                        event
                    ) =>
                        handleFilterChange(
                            setStatus,
                            event
                                .target
                                .value
                        )
                    }
                >

                    <option value="CONFIRMED">
                        Confirmed
                    </option>

                    <option value="ALL">
                        All Status
                    </option>

                    <option value="DRAFT">
                        Draft
                    </option>

                    <option value="CANCELLED">
                        Cancelled
                    </option>

                </select>


                {/* CUSTOMER TYPE */}

                <select
                    value={
                        customerType
                    }
                    onChange={(
                        event
                    ) =>
                        handleFilterChange(
                            setCustomerType,
                            event
                                .target
                                .value
                        )
                    }
                >

                    <option value="ALL">
                        B2B + B2C
                    </option>

                    <option value="REGISTERED">
                        B2B / Registered
                    </option>

                    <option value="UNREGISTERED">
                        B2C / Unregistered
                    </option>

                </select>


                {/* SUPPLY TYPE */}

                <select
                    value={
                        supplyType
                    }
                    onChange={(
                        event
                    ) =>
                        handleFilterChange(
                            setSupplyType,
                            event
                                .target
                                .value
                        )
                    }
                >

                    <option value="ALL">
                        All Supply
                    </option>

                    <option value="INTRA_STATE">
                        Intra-State
                    </option>

                    <option value="INTER_STATE">
                        Inter-State
                    </option>

                </select>


                {/* TAX TYPE */}

                <select
                    value={
                        taxType
                    }
                    onChange={(
                        event
                    ) =>
                        handleFilterChange(
                            setTaxType,
                            event
                                .target
                                .value
                        )
                    }
                >

                    <option value="ALL">
                        All Tax Types
                    </option>

                    <option value="CGST_SGST">
                        CGST + SGST
                    </option>

                    <option value="IGST">
                        IGST
                    </option>

                </select>


                {/* REFRESH */}

                <button
                    type="button"
                    className="gst-dashboard-button"
                    disabled={
                        loading
                            .salesRegister
                    }
                    onClick={
                        loadData
                    }
                >

                    <FiRefreshCw
                        className={
                            loading
                                .salesRegister
                                ? "gst-dashboard-spinner"
                                : ""
                        }
                    />

                    Refresh

                </button>


                {/* RESET */}

                <button
                    type="button"
                    className="gst-dashboard-button"
                    onClick={
                        handleResetFilters
                    }
                >
                    Reset Filters
                </button>

            </section>


            {/* =====================================================
                SALES TABLE
            ===================================================== */}

            <section className="gst-sales-table-card">

                {loading.salesRegister &&
                records.length === 0 ? (

                    <div className="gst-sales-empty">

                        <FiRefreshCw className="gst-dashboard-spinner" />

                        <strong>
                            Loading Sales GST Register...
                        </strong>

                    </div>

                ) : records.length ===
                0 ? (

                    <div className="gst-sales-empty">

                        <FiFileText />

                        <strong>
                            No Sales GST records found
                        </strong>

                        <span>
                            Try changing the GST period or filters.
                        </span>

                    </div>

                ) : (

                    <div className="gst-sales-table-wrapper">

                        <table className="gst-sales-table">

                            <thead>

                                <tr>

                                    <th>
                                        Invoice
                                    </th>

                                    <th>
                                        Date
                                    </th>

                                    <th>
                                        Customer
                                    </th>

                                    <th>
                                        GSTIN
                                    </th>

                                    <th>
                                        GST Type
                                    </th>

                                    <th>
                                        Place of Supply
                                    </th>

                                    <th>
                                        Supply
                                    </th>

                                    <th>
                                        Tax Type
                                    </th>

                                    <th className="text-right">
                                        Taxable
                                    </th>

                                    <th className="text-right">
                                        CGST
                                    </th>

                                    <th className="text-right">
                                        SGST
                                    </th>

                                    <th className="text-right">
                                        IGST
                                    </th>

                                    <th className="text-right">
                                        Cess
                                    </th>

                                    <th className="text-right">
                                        Output GST
                                    </th>

                                    <th className="text-right">
                                        Invoice Total
                                    </th>

                                    <th>
                                        RCM
                                    </th>

                                    <th>
                                        E-Commerce
                                    </th>

                                    <th>
                                        Status
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {records.map(
                                    (
                                        item,
                                        index
                                    ) => {

                                        const registrationType =
                                            item
                                                ?.customer
                                                ?.registrationType;

                                        const isB2B =
                                            registrationType ===
                                            "REGISTERED";


                                        return (

                                            <tr
                                                key={
                                                    item?._id ||
                                                    index
                                                }
                                            >

                                                {/* INVOICE */}

                                                <td>

                                                    <strong className="gst-sales-document-number">

                                                        {item
                                                            ?.documentNumber ||
                                                            "-"}

                                                    </strong>

                                                </td>


                                                {/* DATE */}

                                                <td>

                                                    {formatDate(
                                                        item
                                                            ?.documentDate
                                                    )}

                                                </td>


                                                {/* CUSTOMER */}

                                                <td>

                                                    <div className="gst-sales-customer">

                                                        <strong>

                                                            {item
                                                                ?.customer
                                                                ?.customerName ||
                                                                "-"}

                                                        </strong>

                                                        {item
                                                            ?.customer
                                                            ?.stateCode && (

                                                            <span>

                                                                State Code:{" "}

                                                                {item
                                                                    .customer
                                                                    .stateCode}

                                                            </span>

                                                        )}

                                                    </div>

                                                </td>


                                                {/* GSTIN */}

                                                <td>

                                                    <span className="gst-sales-gstin">

                                                        {item
                                                            ?.customer
                                                            ?.gstin ||
                                                            "-"}

                                                    </span>

                                                </td>


                                                {/* B2B/B2C */}

                                                <td>

                                                    <span
                                                        className={
                                                            isB2B
                                                                ? "gst-sales-customer-type gst-sales-b2b"
                                                                : "gst-sales-customer-type gst-sales-b2c"
                                                        }
                                                    >

                                                        {isB2B
                                                            ? "B2B"
                                                            : "B2C"}

                                                    </span>

                                                </td>


                                                {/* PLACE OF SUPPLY */}

                                                <td>

                                                    <div className="gst-sales-place">

                                                        <strong>

                                                            {item
                                                                ?.placeOfSupply ||
                                                                "-"}

                                                        </strong>

                                                        {item
                                                            ?.placeOfSupplyStateCode && (

                                                            <span>

                                                                {item
                                                                    .placeOfSupplyStateCode}

                                                            </span>

                                                        )}

                                                    </div>

                                                </td>


                                                {/* SUPPLY TYPE */}

                                                <td>

                                                    <span
                                                        className={
                                                            item
                                                                ?.supplyType ===
                                                            "INTER_STATE"
                                                                ? "gst-sales-supply gst-sales-inter"
                                                                : "gst-sales-supply gst-sales-intra"
                                                        }
                                                    >

                                                        {formatText(
                                                            item
                                                                ?.supplyType
                                                        )}

                                                    </span>

                                                </td>


                                                {/* TAX TYPE */}

                                                <td>

                                                    <span className="gst-sales-tax-type">

                                                        {formatText(
                                                            item
                                                                ?.taxType
                                                        )}

                                                    </span>

                                                </td>


                                                {/* TAXABLE */}

                                                <td className="text-right">

                                                    {formatCurrency(
                                                        item
                                                            ?.taxableAmount
                                                    )}

                                                </td>


                                                {/* CGST */}

                                                <td className="text-right">

                                                    {formatCurrency(
                                                        item
                                                            ?.cgstAmount
                                                    )}

                                                </td>


                                                {/* SGST */}

                                                <td className="text-right">

                                                    {formatCurrency(
                                                        item
                                                            ?.sgstAmount
                                                    )}

                                                </td>


                                                {/* IGST */}

                                                <td className="text-right">

                                                    {formatCurrency(
                                                        item
                                                            ?.igstAmount
                                                    )}

                                                </td>


                                                {/* CESS */}

                                                <td className="text-right">

                                                    {formatCurrency(
                                                        item
                                                            ?.cessAmount
                                                    )}

                                                </td>


                                                {/* OUTPUT TAX */}

                                                <td className="text-right">

                                                    <strong className="gst-sales-output-tax">

                                                        {formatCurrency(
                                                            item
                                                                ?.taxAmount
                                                        )}

                                                    </strong>

                                                </td>


                                                {/* GRAND TOTAL */}

                                                <td className="text-right">

                                                    <strong>

                                                        {formatCurrency(
                                                            item
                                                                ?.grandTotal
                                                        )}

                                                    </strong>

                                                </td>


                                                {/* RCM */}

                                                <td>

                                                    <span
                                                        className={
                                                            item
                                                                ?.reverseCharge
                                                                ? "gst-sales-flag gst-sales-flag-yes"
                                                                : "gst-sales-flag gst-sales-flag-no"
                                                        }
                                                    >

                                                        {item
                                                            ?.reverseCharge
                                                            ? "YES"
                                                            : "NO"}

                                                    </span>

                                                </td>


                                                {/* E-COMMERCE */}

                                                <td>

                                                    <div className="gst-sales-ecommerce">

                                                        <span
                                                            className={
                                                                item
                                                                    ?.ecommerce
                                                                    ? "gst-sales-flag gst-sales-flag-yes"
                                                                    : "gst-sales-flag gst-sales-flag-no"
                                                            }
                                                        >

                                                            {item
                                                                ?.ecommerce
                                                                ? "YES"
                                                                : "NO"}

                                                        </span>


                                                        {item
                                                            ?.ecommerce &&
                                                        item
                                                            ?.ecommerceGstin && (

                                                            <small>

                                                                {item
                                                                    .ecommerceGstin}

                                                            </small>

                                                        )}

                                                    </div>

                                                </td>


                                                {/* STATUS */}

                                                <td>

                                                    <span
                                                        className={`gst-sales-status gst-sales-status-${String(
                                                            item
                                                                ?.documentStatus ||
                                                                ""
                                                        ).toLowerCase()}`}
                                                    >

                                                        {item
                                                            ?.documentStatus ||
                                                            "-"}

                                                    </span>

                                                </td>

                                            </tr>

                                        );

                                    }
                                )}

                            </tbody>

                        </table>

                    </div>

                )}


                {/* =================================================
                    PAGINATION
                ================================================= */}

                <div className="gst-sales-pagination">

                    <div>

                        Showing{" "}

                        <strong>
                            {fromRecord}
                        </strong>

                        {" - "}

                        <strong>
                            {toRecord}
                        </strong>

                        {" of "}

                        <strong>
                            {total}
                        </strong>

                    </div>


                    <div className="gst-sales-pagination-controls">

                        <select
                            value={
                                limit
                            }
                            onChange={(
                                event
                            ) => {
                                setLimit(
                                    Number(
                                        event
                                            .target
                                            .value
                                    )
                                );

                                setPage(
                                    1
                                );
                            }}
                        >

                            <option value={10}>
                                10 / page
                            </option>

                            <option value={20}>
                                20 / page
                            </option>

                            <option value={50}>
                                50 / page
                            </option>

                            <option value={100}>
                                100 / page
                            </option>

                        </select>


                        <button
                            type="button"
                            disabled={
                                currentPage <=
                                1
                            }
                            onClick={() =>
                                setPage(
                                    (
                                        current
                                    ) =>
                                        Math.max(
                                            current -
                                                1,
                                            1
                                        )
                                )
                            }
                        >
                            <FiChevronLeft />
                        </button>


                        <span>

                            Page{" "}

                            <strong>
                                {currentPage}
                            </strong>

                            {" of "}

                            <strong>
                                {totalPages}
                            </strong>

                        </span>


                        <button
                            type="button"
                            disabled={
                                currentPage >=
                                totalPages
                            }
                            onClick={() =>
                                setPage(
                                    (
                                        current
                                    ) =>
                                        Math.min(
                                            current +
                                                1,
                                            totalPages
                                        )
                                )
                            }
                        >
                            <FiChevronRight />
                        </button>

                    </div>

                </div>

            </section>

        </div>
    );
};


export default SalesGstRegister;